import { UserGroup, UserGroupingDefinition, GroupKeyComparatorHelpers, UserComparatorHelpers, GroupUserValueHelpers } from "../search-results/result-group";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { AppService } from "@suite/app.service";
import { UserStaffInfo } from "@common/data-models/service/user-staff-info";
import { InvitationStatusCodes } from "@common/utils/util";


export class UsersResults {
    public users: Array<UserStaffInfo> = [];
    public newUsers: Array<UserStaffInfo> = [];
    
    public usersGroups: { [id: string]: UserGroup<UserStaffInfo> } = {};

    private cancelRebuildGroupsFlag: boolean = false;

    public static appService: AppService = null;
    public static DefaultGrouping: string = PSTFieldNames.UserInfoStatus;
    public static DefaultSorting: string = PSTFieldNames.UserInfoID;
    
    public static groupByField: string = PSTFieldNames.UserInfoStatus;
    public static sortByField: string = PSTFieldNames.UserInfoID;

    private static _userGroupDefs: { [id: string]: UserGroupingDefinition } = {};
    public static get userGroupDefs(): { [id: string]: UserGroupingDefinition } {
        if ((Object.keys(UsersResults._userGroupDefs).length == 0) && UsersResults.appService) {
            UsersResults._userGroupDefs[PSTFieldNames.UserInfoID] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoID, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.String(PSTFieldNames.UserInfoID)
            );

            UsersResults._userGroupDefs[PSTFieldNames.UserInfoProgram] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoProgram, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.Generic(PSTFieldNames.UserInfoProgram)
            );

            UsersResults._userGroupDefs[PSTFieldNames.UserInfoGivenName] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoGivenName, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.String(PSTFieldNames.UserInfoGivenName)
            );

            UsersResults._userGroupDefs[PSTFieldNames.UserInfoSurname] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoSurname, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.String(PSTFieldNames.UserInfoSurname)
            );

            UsersResults._userGroupDefs[PSTFieldNames.UserInfoStatus] = new UserGroupingDefinition(
                GroupUserValueHelpers.UserInfoStatusStringOrDefault(PSTFieldNames.UserInfoStatus, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.Generic(PSTFieldNames.UserInfoActive)
            );
            
            UsersResults._userGroupDefs[PSTFieldNames.UserInfoRole] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoRole, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.Role()
            );
            
            UsersResults._userGroupDefs[PSTFieldNames.UserInfoEmail] = new UserGroupingDefinition(
                GroupUserValueHelpers.StringOrDefault(PSTFieldNames.UserInfoEmail, "No value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                UserComparatorHelpers.String(PSTFieldNames.UserInfoEmail)
            );
        }
        return UsersResults._userGroupDefs;
    }
    
    public dispose() {
        this.users = [];
        this.usersGroups = {};
        this.newUsers = [];
    }

    public addUser(user: UserStaffInfo) {
        if (user.InvitationStatus == InvitationStatusCodes.NEW
        ) { // TODO check new status
            this.newUsers.push(user);
        } else {
            this.users.push(user);
        }
    }

    public checkUserExists(userID: string): boolean {
        for (let user of this.users) {
            if (user.UserID.toUpperCase() == userID.toUpperCase()) return true;
        }
        for (let user of this.newUsers) {
            if (user.UserID.toUpperCase() == userID.toUpperCase()) return true;
        }

        return false;
    }

    public removeUser(user: UserStaffInfo) {
        if (user && user.UserID) {
            for (let i = 0; i < this.users.length; i++) {
                if (user.UserID == this.users[i].UserID) {
                    this.users.splice(i, 1);
                    for (let group in UsersResults.userGroupDefs) {
                        if (this.usersGroups[group]) this.usersGroups[group].remove(UsersResults.userGroupDefs[group].valueConvertor(user),user);
                    }
                    return;
                }
            }
            for (let i = 0; i < this.newUsers.length; i++) {
                if (user.UserID == this.newUsers[i].UserID) {
                    this.newUsers.splice(i, 1);
                    return;
                }
            }
        }
    }

    public updateUser(user: UserStaffInfo) {
        for (let i = 0; i < this.users.length; i++) {
            if (user.UserID == this.users[i].UserID) {
                this.users[i] = user;
                return;
            }
        }
    }

    public sortGroups(sortBy: any) {
        let keys = UsersResults.appService.groupByUsersKeys;
        for (let field of keys) {
            this.usersGroups[field].sort(UsersResults.userGroupDefs[field].groupKeyComparator("asc"), UsersResults.userGroupDefs[sortBy].userComparator("asc"));
        }
    }

    public rebuildGroups(groupBy: string, sortBy: any) {
        let groups = UsersResults.appService.groupByUsersKeys;       
        // Clear all groups
        for (let field of groups) {
            if (!this.usersGroups[field]) this.usersGroups[field] = new UserGroup<UserStaffInfo>();
            this.usersGroups[field].clear();
        }
        if (!this.usersGroups[UsersResults.groupByField]) {
            UsersResults.groupByField = groupBy;
        }
        // Build active group
        for (let user of this.users) {
            this.usersGroups[UsersResults.groupByField].addToGroups(UsersResults.userGroupDefs[UsersResults.groupByField].valueConvertor(user), user);
            if (this.cancelRebuildGroupsFlag) {
                this.cancelRebuildGroupsFlag = false;
                return;
            }
        }
        for (let user of this.newUsers) {
            this.usersGroups[UsersResults.groupByField].addToGroups(UsersResults.userGroupDefs[UsersResults.groupByField].valueConvertor(user), user);
            if (this.cancelRebuildGroupsFlag) {
                this.cancelRebuildGroupsFlag = false;
                return;
            }
        }
        this.usersGroups[UsersResults.groupByField].sort(UsersResults.userGroupDefs[UsersResults.groupByField].groupKeyComparator("asc"), UsersResults.userGroupDefs[sortBy].userComparator("asc"));
        this.usersGroups[UsersResults.groupByField].isBuilt = true;

        // Build the rest of the groups
        for (let field of groups) {
            if (!this.usersGroups[field]) {
                this.cancelRebuildGroupsFlag = false;
                return;
            }
            if (this.usersGroups[field].isBuilt) {
                continue;
            }
            let i = 0;
            for (let user of this.users) {
                this.usersGroups[field].addToGroups(UsersResults.userGroupDefs[field].valueConvertor(user), user);
            }
            for (let user of this.newUsers) {
                this.usersGroups[field].addToGroups(UsersResults.userGroupDefs[field].valueConvertor(user), user);
            }
            this.usersGroups[field].sort(UsersResults.userGroupDefs[field].groupKeyComparator("asc"), UsersResults.userGroupDefs[sortBy].userComparator("asc"));
            this.usersGroups[field].isBuilt = true;
        }
    }
}
